import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { getHttpClient, HttpClient, useHttpClient } from '@bonusly/http-client';
import { getQueryClient } from '@bonusly/query-client';

import { UserVote, VotingView } from '../schemas/nomination-voting';
import { listOf } from '../schemas/utils';

enum QueryKey {
	VotingView = 'nominations:voting-view',
}

export const nominationVotingViewQueryKey = (id: string) => [QueryKey.VotingView, id];

export const nominationVotingPeriodPath = (id: string, prefix = '') => `${prefix}/nomination_voting_periods/${id}`;

export const nominationVotesPath = (id: string, prefix = '') => `${prefix}/nomination_voting_periods/${id}/votes`;

const createNominationVotingViewFetcher = (http: HttpClient, id: string) => async () => {
	const res = await http.get(nominationVotingPeriodPath(id), {
		headers: {
			'Key-Inflection': 'camel',
		},
	});

	return VotingView.parse(res.data);
};

export const useNominationVotingView = (id: string) => {
	return useQuery({
		queryKey: nominationVotingViewQueryKey(id),
		queryFn: createNominationVotingViewFetcher(useHttpClient(), id),
	});
};

export const loadNominationVotingView = async (id: string) => {
	const http = await getHttpClient();
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: nominationVotingViewQueryKey(id),
		queryFn: createNominationVotingViewFetcher(http, id),
	});
};

export const useStoreNominationVotingView = () => {
	const queryClient = useQueryClient();

	return useCallback(
		(view: VotingView) => {
			queryClient.setQueryData<VotingView>(nominationVotingViewQueryKey(view.nominationPeriodId), view);
		},
		[queryClient]
	);
};

export const useFetchNominationVotingView = () => {
	const queryClient = useQueryClient();

	return useCallback((id: string) => queryClient.getQueryData<VotingView>(nominationVotingViewQueryKey(id)), [queryClient]);
};

type CastVotesOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: (votes: UserVote[]) => void;
};

/**
 * Casts (replaces) a voter's votes: send the full set of selected nominee
 * uniqueness keys, and the server will destroy the voter's existing votes for
 * the period and create the new set in one logical operation. Pass an empty
 * array to clear all votes.
 *
 * The server returns only the voter's votes (casting never changes the nominee
 * list or anonymized responses), so we merge them into the cached view's
 * `userVotes` rather than refetching the whole view.
 */
export const useCastVotes = (id: string, options: CastVotesOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreNominationVotingView();
	const fetchView = useFetchNominationVotingView();

	return useMutation({
		mutationFn: async (nomineeUniquenessKeys: string[]) => {
			try {
				const res = await http.put(
					nominationVotesPath(id),
					{ nomineeUniquenessKeys },
					{
						headers: {
							'Key-Inflection': 'camel',
						},
					}
				);

				const userVotes = listOf(UserVote).parse(res.data);
				const existing = fetchView(id);
				if (existing) {
					store({ ...existing, userVotes });
				}
				options.onSuccess?.(userVotes);
				return userVotes;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};
